import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Button, Avatar, Heading, Box, List, Text, Flex } from "samba-ui";
import AddNewGroupPermissionModal from "./AddNewGroupPermissionModal";
import AreYouSureModal from "./AreYouSureModal";
import Popup from "reactjs-popup";

const AvatarBox = styled(Box)`
  position: relative;
  border: 2px solid #fff;
  border-radius: 50%;
  overflow: hidden;
  &:not(:first-child) {
    margin-left: -15px;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const GroupPermissionList = ({
  permissions,
  onSubmit,
  onUpdate,
  onDelete,
  isSuperOperator
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);

  const onUpdateClick = selectedPermissionId => {
    setSelectedPermissionId(selectedPermissionId);
    setIsOpen(true);
  };

  const onDeleteClick = selectedPermissionId => {
    setSelectedPermissionId(selectedPermissionId);
    setIsDeleteOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setIsDeleteOpen(false);
    setSelectedPermissionId(null);
  };

  return (
    <Box>
      <Flex mb={3}>
        <Heading color="secondary" fontSize={3} fontWeight={500}>
          All Group Permissions
        </Heading>
      </Flex>
      <List.Header borderColor="#d6d6d6 !important">
        <Flex alignItems="center">
          <Box width={2 / 10}>
            <Text fontSize={1} color="secondary" fontWeight={500}>
              Group Permission
            </Text>
          </Box>
          <Box width={5 / 10}>
            <Text fontSize={1} color="secondary" fontWeight={500}>
              Users
            </Text>
          </Box>
          <Box width={1 / 10}>
            <Text fontSize={1} color="secondary" fontWeight={500}>
              Internal
            </Text>
          </Box>
          <Box width={2 / 10}>
            <Button
              variant="primaryBordered"
              onClick={() => setIsOpen(true)}
              disabled={isSuperOperator}
            >
              + New Group Permission
            </Button>
          </Box>
        </Flex>
      </List.Header>
      {!Object.keys(permissions).length ? (
        <List.Empty>No result found!</List.Empty>
      ) : null}
      {Object.keys(permissions).map(id => {
        return (
          <List.Item key={id} borderColor="#d6d6d6 !important">
            <Flex alignItems="center">
              <Box width={2 / 10}>
                <Text fontSize={1} color="palette.ink.normal">
                  {permissions[id].name}
                </Text>
              </Box>
              <Box width={5 / 10}>
                <Box
                  style={{
                    display: "inline-flex",
                    flexWrap: "wrap",
                    justifyContent: "center"
                  }}
                >
                  {permissions[id].users.map(user => {
                    // const user = users[id];
                    if (!user) {
                      return <></>;
                    }
                    return (
                      <Popup
                        key={user.userId}
                        contentStyle={{
                          width: 225,
                          borderRadius: 4,
                          padding: 0,
                          borderColor: "#CFD6E6"
                        }}
                        trigger={() => (
                          <AvatarBox key={id} as="span">
                            <Avatar
                              src={user.imageURL}
                              width={40}
                              height={40}
                            />
                          </AvatarBox>
                        )}
                        on="hover"
                        position="top center"
                        closeOnDocumentClick
                      >
                        <Flex p={2}>
                          <Avatar
                            src={user.imageURL}
                            mr={2}
                            width={40}
                            height={40}
                          />
                          <Box>
                            <Box>
                              <Text fontSize={1} color="palette.ink.normal">
                                {user.name}
                              </Text>
                            </Box>
                            <Text fontSize={1} color="palette.ink.normal">
                              {user.email}
                            </Text>
                          </Box>
                        </Flex>
                      </Popup>
                    );
                  })}
                </Box>
              </Box>
              <Box width={1 / 10}>
                <Checkbox
                  type="checkbox"
                  checked={permissions[id].isInternal}
                />
              </Box>
              <Flex
                width={2 / 10}
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  variant="destructiveBordered"
                  onClick={() => onDeleteClick(id)}
                  disabled={isSuperOperator || permissions[id].inUse}
                >
                  Delete
                </Button>
                <Button
                  variant="primaryBordered"
                  onClick={() => onUpdateClick(id)}
                  disabled={isSuperOperator}
                >
                  Update
                </Button>
              </Flex>
            </Flex>
          </List.Item>
        );
      })}
      <AreYouSureModal
        isOpen={isDeleteOpen}
        onClose={onClose}
        title="Delete Permission"
        body="Are you sure you want to delete this group permission?"
        onSubmit={() => onDelete(selectedPermissionId)}
      />
      <AddNewGroupPermissionModal
        isOpen={isOpen}
        selectedPermission={
          selectedPermissionId ? permissions[selectedPermissionId] : null
        }
        onClose={onClose}
        onSubmit={body => {
          onSubmit(body);
          onClose();
        }}
        onUpdate={(id, body) => {
          onUpdate(id, body);
          onClose();
        }}
      />
    </Box>
  );
};

GroupPermissionList.propTypes = {
  permissions: PropTypes.object.isRequired,
  users: PropTypes.object.isRequired,
  isSuperOperator: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default GroupPermissionList;
